import React, { Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import UserHeader from './useHeader';
import Sidebar from './Sidebar';
import { useNavigation, VIEWS } from '../../context/NavigationContext';
import { useAuth } from '../../context/AuthContext';
import VehicleList from '../VehicleLog/VehicleList';
import { lazyWithRetry } from '../../utils/lazyWithRetry';

const DashboardModule         = lazyWithRetry(() => import('../modules/DashboardModule'));
const ControlManualModule     = lazyWithRetry(() => import('../modules/ControlManualModule'));
const SuscriptoresModule      = lazyWithRetry(() => import('../modules/SuscriptoresModule'));
const PlacasAutorizadasModule = lazyWithRetry(() => import('../modules/PlacasAutorizadasModule'));
const ConfiguracionesModule   = lazyWithRetry(() => import('../modules/ConfiguracionesModule'));
const TarifasModule           = lazyWithRetry(() => import('../modules/TarifasModule'));
const SalidasProgramadasModule = lazyWithRetry(() => import('../modules/SalidasProgramadasModule'));
const UsuariosModule          = lazyWithRetry(() => import('../modules/UsuariosModule'));
const ReporteriaModule        = lazyWithRetry(() => import('../modules/ReporteriaModule'));
const CambioTurnoModule       = lazyWithRetry(() => import('../modules/CambioTurnoModule'));
const EscapesModule           = lazyWithRetry(() => import('../modules/EscapesModule'));
const ServiciosModule         = lazyWithRetry(() => import('../modules/ServiciosModule'));
const PagosPendientesModule   = lazyWithRetry(() => import('../modules/PagosPendientesModule'));
const SucursalesModule        = lazyWithRetry(() => import('../modules/SucursalesModule'));

function Fallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, height: '100%' }}>
      <CircularProgress sx={{ color: '#F59E0B' }} />
    </Box>
  );
}

function CurrentView() {
  const { currentView } = useNavigation();
  const wrap = (Module) => <Suspense fallback={<Fallback />}><Module /></Suspense>;

  switch (currentView) {
    case VIEWS.DASHBOARD:          return wrap(DashboardModule);
    case VIEWS.BITACORA:           return <VehicleList />;
    case VIEWS.CONTROL_MANUAL:     return wrap(ControlManualModule);
    case VIEWS.SUSCRIPTORES:       return wrap(SuscriptoresModule);
    case VIEWS.PLACAS_AUTORIZADAS: return wrap(PlacasAutorizadasModule);
    case VIEWS.CONFIGURACIONES:    return wrap(ConfiguracionesModule);
    case VIEWS.TARIFAS:            return wrap(TarifasModule);
    case VIEWS.SALIDAS_PROGRAMADAS: return wrap(SalidasProgramadasModule);
    case VIEWS.USUARIOS:           return wrap(UsuariosModule);
    case VIEWS.REPORTERIA:         return wrap(ReporteriaModule);
    case VIEWS.CAMBIO_TURNO:       return wrap(CambioTurnoModule);
    case VIEWS.ESCAPES:            return wrap(EscapesModule);
    case VIEWS.SERVICIOS:          return wrap(ServiciosModule);
    case VIEWS.PAGOS_PENDIENTES:   return wrap(PagosPendientesModule);
    case VIEWS.SUCURSALES:         return wrap(SucursalesModule);
    default:                       return wrap(DashboardModule);
  }
}

export default function MainLayout() {
  const { user } = useAuth();
  const clienteActual = user?.cliente_id;
  // Entrar a otra sucursal también cambia los datos: se remonta igual que al cambiar de parqueadero.
  const sucursalActual = user?.sucursal_id;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#F4F4F4' }}>
      <UserHeader />
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Sidebar />
        <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {/* La key fuerza a React a remontar la vista al cambiar de cliente.
              Los módulos cargan sus datos con useCallback([]) y un useEffect que
              depende de esa función, así que sin remontar conservaban los datos
              del cliente anterior hasta recargar la página. Resolverlo aquí
              cubre todas las pantallas, incluidas las que se agreguen después. */}
          <CurrentView key={`${clienteActual ?? 'sin-cliente'}:${sucursalActual ?? 'todas'}`} />
        </Box>
      </Box>
    </Box>
  );
}
